'use client'

import { Popover as BasePopover } from '@base-ui/react/popover'
import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef, ReactElement, ReactNode, RefObject } from 'react'

import { useMobileSheet } from '../mobile-sheet'
import { mobileSheetPosition, type ControlledOpenState, type OverlayPositionerProps } from '../overlay'
import { overlayStyles } from '../overlay'
import { fonts } from '../tokens/tokens.stylex'

const styles = stylex.create({
  font: { fontFamily: fonts.sans },
})

type PopoverContent = { children: ReactNode }

export type PopoverProps = PopoverContent & ControlledOpenState

/**
 * Use for contextual content with its own controls, such as destination search. Choose Menu for a list of
 * actions or Dialog for a modal task. Applications supply content and own selection, requests, and controlled
 * visibility. Status: supported.
 */
export function Popover({ onOpenChange, ...props }: PopoverProps) {
  return <BasePopover.Root {...props} onOpenChange={onOpenChange} />
}

type PopoverTriggerOptions = {
  render?: ReactElement
  variant?: 'plain' | 'row'
}

export type PopoverTriggerProps = Omit<ComponentPropsWithRef<'button'>, 'className' | 'style' | 'color'> &
  PopoverTriggerOptions

/**
 * Use within Popover to disclose contextual controls; choose MenuTrigger for an action list. Applications
 * provide an accessible name and may supply a composed trigger element through render. Status: supported.
 */
export function PopoverTrigger({ variant = 'plain', ...props }: PopoverTriggerProps) {
  return (
    <BasePopover.Trigger
      {...props}
      {...stylex.props(overlayStyles.trigger, styles.font, variant === 'row' && overlayStyles.row)}
    />
  )
}

export type PopoverPortalProps = { children: ReactNode }

/**
 * Use with PopoverPositioner and PopoverPopup to render contextual content outside clipping containers.
 * Applications set their theme on the document root because body portals inherit that theme. Use MenuPortal for
 * action menus. Status: supported.
 */
export function PopoverPortal({ children }: PopoverPortalProps) {
  return <BasePopover.Portal>{children}</BasePopover.Portal>
}

export type PopoverPositionerProps = OverlayPositionerProps<8 | 10>

/**
 * Use to anchor PopoverPopup to its trigger while preserving collision placement. Applications choose placement;
 * mobileSheet must be enabled on both the positioner and popup for the coarse-pointer panel. Use MenuPositioner
 * for action menus. Status: supported.
 */
export function PopoverPositioner({ mobileSheet = false, sideOffset = 8, ...props }: PopoverPositionerProps) {
  const sheet = useMobileSheet(mobileSheet)

  return (
    <BasePopover.Positioner
      {...props}
      sideOffset={sideOffset}
      collisionPadding={8}
      {...stylex.props(overlayStyles.positioner)}
      style={sheet ? mobileSheetPosition : undefined}
    />
  )
}

type ExplicitPopoverLabel = { 'aria-label': string; 'aria-labelledby'?: never }

type ReferencedPopoverLabel = { 'aria-label'?: never; 'aria-labelledby': string }

type PopoverName = ExplicitPopoverLabel | ReferencedPopoverLabel

type PopoverPopupOptions = {
  initialFocus?: boolean | RefObject<HTMLElement | null>
  finalFocus?: boolean | RefObject<HTMLElement | null> | (() => HTMLElement | null)
  size?: 'default' | 'compact'
  mobileSheet?: boolean
}

export type PopoverPopupProps = Omit<
  ComponentPropsWithRef<'div'>,
  'className' | 'style' | 'color' | 'aria-label' | 'aria-labelledby'
> &
  PopoverName &
  PopoverPopupOptions

/**
 * Use for named contextual content containing controls; choose MenuPopup for actions or Dialog for a modal task.
 * Applications provide an accessible name, content, and selection handlers. Keep mobileSheet consistent with
 * PopoverPositioner. Status: supported.
 */
export function PopoverPopup({ size = 'default', mobileSheet = false, ...props }: PopoverPopupProps) {
  const sheet = useMobileSheet(mobileSheet)

  return (
    <BasePopover.Popup
      {...props}
      {...stylex.props(
        overlayStyles.popup,
        styles.font,
        size === 'compact' && overlayStyles.compact,
        sheet && overlayStyles.sheet
      )}
    />
  )
}
